import { useState, useRef, useEffect } from "react"
import { GoogleGenerativeAI } from "@google/generative-ai"
import ReactMarkdown from "react-markdown"
import { Send, Copy, Check, Clock, LogIn } from "lucide-react"
import { profiles, defaultProfile } from '../data/profiles'
import { useAuth } from '../contexts/AuthContext'

interface ChatMessage {
  role: "user" | "assistant"
  content: string
  isLoading?: boolean
  isError?: boolean
}

interface VideoInfo {
  title: string
  url: string
  channelName?: string
}

interface TranscriptChatProps {
  videoUrl: string
  initialTranscript: string | Array<{ text: string; timestamp: string }>
  initialVideoTitle: string
}

export default function TranscriptChat({ videoUrl, initialTranscript, initialVideoTitle }: TranscriptChatProps) {
  const { user } = useAuth()
  const [messages, setMessages] = useState<ChatMessage[]>(() => {
    const savedMessages = localStorage.getItem('chatMessages')
    return savedMessages ? JSON.parse(savedMessages) : []
  })
  const [input, setInput] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [loadingSeconds, setLoadingSeconds] = useState(0)
  const [cooldownSeconds, setCooldownSeconds] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState<{ [key: string]: boolean }>({})
  const [videoInfo, setVideoInfo] = useState<VideoInfo>({
    title: initialVideoTitle,
    url: videoUrl,
    channelName: ''
  })
  const [activeProfile, setActiveProfile] = useState(() => {
    const savedProfile = localStorage.getItem('activeProfile')
    return savedProfile ? JSON.parse(savedProfile) : defaultProfile
  })
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const playerRef = useRef<any>(null)

  // Save messages to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('chatMessages', JSON.stringify(messages))
  }, [messages])

  // Save active profile to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('activeProfile', JSON.stringify(activeProfile))
  }, [activeProfile])

  // Clear chat history when video changes
  useEffect(() => {
    if (videoUrl) {
      setMessages([])
      localStorage.removeItem('chatMessages')
    }
  }, [videoUrl])

  useEffect(() => {
    const fetchVideoInfo = async () => {
      try {
        const url = `https://www.youtube.com/oembed?url=${videoUrl}&format=json`;
        const response = await fetch(url);
        if (!response.ok) throw new Error('Could not fetch video info');
        const data = await response.json();
        setVideoInfo(prev => ({
          ...prev,
          title: data.title,
          channelName: data.author_name || ''
        }));
        
        // Add initial greeting message
        setMessages([{
          role: "assistant",
          content: `Hey, I'm ready to talk about "${data.title}" by ${data.author_name || 'Unknown'}`
        }]);
      } catch (err) {
        console.error("Error fetching video info:", err);
      }
    };

    if (videoUrl) {
      fetchVideoInfo();
    }
  }, [videoUrl]);

  // Handle cooldown timer
  useEffect(() => {
    if (cooldownSeconds > 0) {
      const timer = setInterval(() => {
        setCooldownSeconds(prev => prev - 1)
      }, 1000)
      return () => clearInterval(timer)
    }
  }, [cooldownSeconds])

  // Handle loading counter
  useEffect(() => {
    let timer: number
    if (isLoading) {
      setLoadingSeconds(0)
      timer = window.setInterval(() => {
        setLoadingSeconds(prev => prev + 1)
      }, 1000)
    } else {
      setLoadingSeconds(0)
    }
    return () => window.clearInterval(timer)
  }, [isLoading])

  // Initialize YouTube API
  useEffect(() => {
    // Load YouTube API if not already loaded
    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);

      // Define the onYouTubeIframeAPIReady callback
      window.onYouTubeIframeAPIReady = initPlayer;
    } else {
      initPlayer();
    }

    function initPlayer() {
      // Instead of creating a new player, just get the reference to the existing iframe
      const existingPlayer = document.querySelector('iframe');
      if (existingPlayer && window.YT && window.YT.Player) {
        playerRef.current = new window.YT.Player(existingPlayer);
      }
    }

    // Cleanup
    return () => {
      if (playerRef.current) {
        // Don't destroy the player, just remove the reference
        playerRef.current = null;
      }
    };
  }, []); // Remove videoUrl dependency to prevent reinitializing

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      const chatContainer = messagesEndRef.current.parentElement
      if (chatContainer) {
        chatContainer.scrollTop = chatContainer.scrollHeight
      }
    }
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  // Format transcript for AI
  const formatTranscript = (transcript: string | Array<{ text: string; timestamp: string }>): string => {
    if (typeof transcript === 'string') return transcript;
    return transcript
      .map(item => {
        // Normalize timestamp format
        const timestamp = item.timestamp.replace(/^(\d{1,2}):(\d{2})$/, '00:$1:$2');
        return `[${timestamp}] ${item.text}`;
      })
      .join('\n');
  };

  const handleSeek = (timestamp: string) => {
    if (!playerRef.current) {
      // Try to initialize player if not already initialized
      const existingPlayer = document.querySelector('iframe');
      if (existingPlayer && window.YT && window.YT.Player) {
        playerRef.current = new window.YT.Player(existingPlayer);
      }
      
      if (!playerRef.current) {
        console.warn('YouTube player not initialized');
        return;
      }
    }

    // Convert timestamp to seconds
    const parts = timestamp.split(':').map(Number);
    const totalSeconds = (parts.length === 3)
      ? parts[0] * 3600 + parts[1] * 60 + parts[2]
      : parts[0] * 60 + parts[1];

    try {
      console.log('Seeking to:', totalSeconds, 'seconds');
      playerRef.current.seekTo(totalSeconds, true);
      playerRef.current.playVideo();
    } catch (err) {
      console.error('Error seeking to timestamp:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!input.trim() || isLoading || cooldownSeconds > 0) return
  
    const userMessage = input.trim()
    const isTweetRequest = /tweet/i.test(userMessage)
  
    let formattedTranscript = ''
    try {
      formattedTranscript = formatTranscript(initialTranscript)
    } catch (err) {
      console.error('Error formatting transcript:', err)
      formattedTranscript = typeof initialTranscript === 'string' ? initialTranscript : JSON.stringify(initialTranscript)
    }
  
    setInput("")
    setMessages(prev => [
      ...prev,
      { role: "user", content: userMessage },
      { role: "assistant", content: "", isLoading: true }
    ])
    setIsLoading(true)
  
    try {
      const genAI = new GoogleGenerativeAI(import.meta.env.VITE_GEMINI_API_KEY)
      const model = genAI.getGenerativeModel({ model: "gemini-2.0-flash-lite" })

      // Format previous messages for context, ensuring proper string conversion
      const previousMessages = messages.map(msg => 
        `${msg.role === 'user' ? 'User' : activeProfile.name}: ${typeof msg.content === 'object' ? JSON.stringify(msg.content) : String(msg.content)}`
      ).join('\n\n')

      // Helper function to safely stringify any value
      const safeStringify = (value: any): string => {
        if (value === null || value === undefined) return ''
        if (typeof value === 'object') {
          if (Array.isArray(value)) {
            return value.map(safeStringify).join('\n')
          }
          return Object.values(value).map(safeStringify).join('\n')
        }
        return String(value)
      }
  
      const prompt = `
You are ${activeProfile.name}, a ${activeProfile.data.identity.creator_archetype}. Here's how you write:

Voice & Style:
- Authenticity: ${safeStringify(activeProfile.data.identity.emotional_posture.authenticity)}
- Confidence: ${safeStringify(activeProfile.data.identity.emotional_posture.confidence)}
- Writing Style: ${safeStringify(activeProfile.data.identity.emotional_posture.style)}
- Format: ${safeStringify(activeProfile.data.identity.emotional_posture.format)}

Your core traits:
${Array.isArray(activeProfile.data.identity.core_persona) 
  ? activeProfile.data.identity.core_persona.map(safeStringify).join('\n') 
  : safeStringify(activeProfile.data.identity.core_persona)}

Your signature phrases:
${Array.isArray(activeProfile.data.examples_of_signature_lines) 
  ? activeProfile.data.examples_of_signature_lines.map(safeStringify).join('\n') 
  : safeStringify(activeProfile.data.examples_of_signature_lines)}

STRICT RESPONSE RULES:
- DO NOT answer with anything beyond what the user asked for.
- If they asked for a number (e.g., "3 tweets", "2 paragraphs", "1 sentence"), follow it exactly. No more, no less.
- If they asked for a format (e.g., tweets, carousel, idea list), follow that format only.
- Do not explain your thinking unless asked.
- Do not add context, summaries, or apologies unless explicitly told to.
- Keep it short, punchy, and in character.
- Never say "as an AI" or explain your role.
- Maintain consistent personality throughout the conversation.
- Reference previous messages when relevant.
- Never claim the video is yours - always refer to the actual creator.
- When presenting structured data (like lists with multiple attributes per item), use markdown tables.

FORMATTING RULES:
1. For tweets:
- Always prefix each tweet with "Tweet: "
- Each tweet should be on a new line
- Keep tweets concise and impactful
- NEVER use hashtags in tweets
- Write in natural language only

2. For tables:
- Use markdown table syntax (| Column 1 | Column 2 |)
- Include clear, descriptive column headers
- Keep content concise but informative
- Use consistent formatting for ratings/numbers

3. For chapters/timestamps:
- Use the timestamps from the transcript
- Format as a bullet list with timestamps at the start of each line
- Each timestamp should be in the format that matches the transcript (HH:MM:SS)
- Identify major topic changes or sections for chapters
- Example:
  * 00:00:00 Introduction and Overview
  * 00:02:30 First Main Topic
  * 00:15:45 Second Main Topic
  * 01:23:45 Conclusion

The YouTube video:
- Title: "${videoInfo.title}"
- Creator: ${videoInfo.channelName || 'Unknown'}
- URL: ${videoInfo.url}

Previous conversation:
${previousMessages}

Transcript:
${formattedTranscript}

The user asked: "${userMessage}"

${isTweetRequest
      ? `The user requested tweets. Format them like:
Tweet: your content here

Respond with ONLY tweets. No setup, no summary, no intro.`
      : `The user did NOT request tweets.
Respond casually, in your authentic tone.
Keep it as long or short as the user asked — nothing more.`}
    `.trim()
  
      const result = await model.generateContent(prompt)
      const response = await result.response
      const text = response.text()
  
      if (!text || typeof text !== "string") {
        throw new Error("Invalid response format")
      }
  
      setMessages(prev => prev.slice(0, -1).concat({ role: "assistant", content: text }))
    } catch (err) {
      console.error("Error generating response:", err)
      setMessages(prev => prev.slice(0, -1).concat({ 
        role: "assistant", 
        content: "⚠️ I encountered an error while processing your request. This might be due to high load or temporary issues. Please wait 15 seconds and try again.",
        isError: true
      }))
      setCooldownSeconds(15)
    } finally {
      setIsLoading(false)
    }
  }
  
  // Add keyboard event handler for Enter key
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const copyToClipboard = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(prev => ({ ...prev, [id]: true }))
      setTimeout(() => setCopied(prev => ({ ...prev, [id]: false })), 2000)
    } catch (err) {
      console.error("Failed to copy:", err)
    }
  }

  // Helper function to safely stringify message content for display
  const formatMessageContent = (content: any): string => {
    if (content === null || content === undefined) return ''
    if (typeof content === 'object') {
      return JSON.stringify(content, null, 2)
    }
    return String(content)
  }

  // Function to request timestamps
  const requestTimestamps = async () => {
    if (isLoading || cooldownSeconds > 0) return;
    
    const timestampRequest = "Give me time stamps for the video (include all topics discussed).";
    
    setMessages(prev => [
      ...prev,
      { role: "user", content: timestampRequest },
      { role: "assistant", content: "", isLoading: true }
    ]);
    setIsLoading(true);
    
    try {
      const genAI = new GoogleGenerativeAI(import.meta.env.VITE_GEMINI_API_KEY);
      const model = genAI.getGenerativeModel({ model: "gemini-2.0-flash-lite" });

      const previousMessages = messages.map(msg => 
        `${msg.role === 'user' ? 'User' : activeProfile.name}: ${typeof msg.content === 'object' ? JSON.stringify(msg.content) : String(msg.content)}`
      ).join('\n\n');

      let formattedTranscript = '';
      try {
        formattedTranscript = formatTranscript(initialTranscript);
      } catch (err) {
        console.error('Error formatting transcript:', err);
        formattedTranscript = typeof initialTranscript === 'string' ? initialTranscript : JSON.stringify(initialTranscript);
      }

      const prompt = `
You are ${activeProfile.name}, a ${activeProfile.data.identity.creator_archetype}. Here's how you write:

Voice & Style:
- Authenticity: ${activeProfile.data.identity.emotional_posture.authenticity}
- Confidence: ${activeProfile.data.identity.emotional_posture.confidence}
- Writing Style: ${activeProfile.data.identity.emotional_posture.style}
- Format: ${activeProfile.data.identity.emotional_posture.format}

Your core traits:
${Array.isArray(activeProfile.data.identity.core_persona) 
  ? activeProfile.data.identity.core_persona.map((trait: any) => String(trait)).join('\n') 
  : String(activeProfile.data.identity.core_persona)}

STRICT FORMATTING AND CONTENT RULES:

1. Timestamp Format:
- Each line MUST start with a timestamp in the exact format: "MM:SS" or "HH:MM:SS"
- Each line MUST be in this format: "00:00 Description" or "00:00:00 Description"
- Each line MUST be a bullet point (*)
- NO brackets, NO dashes, NO other characters before the timestamp

2. Content Organization:
- Group content into MAJOR TOPICS only
- Each timestamp should mark the start of a new significant topic or segment
- DO NOT timestamp every sentence or small detail
- Combine related discussions into single topics
- Use clear, concise topic descriptions
- Aim for 5-15 main topics for the entire video

3. Example format:
* 00:00 Introduction and Overview
* 05:30 First Major Topic Discussion
* 15:45 Second Key Topic Begins
* 25:20 Main Argument and Analysis
* 45:30 Conclusion and Key Takeaways

The YouTube video:
- Title: "${videoInfo.title}"
- Creator: ${videoInfo.channelName || 'Unknown'}
- URL: ${videoInfo.url}

Previous conversation:
${previousMessages}

Transcript:
${formattedTranscript}

The user asked for timestamps. Provide a concise list of the MAIN TOPICS discussed in the video with their corresponding timestamps. Focus on major topics and themes, not individual statements. Follow the format rules EXACTLY.
`.trim();

      const result = await model.generateContent(prompt);
      const response = await result.response;
      const text = response.text();

      if (!text || typeof text !== "string") {
        throw new Error("Invalid response format");
      }

      setMessages(prev => prev.slice(0, -1).concat({ role: "assistant", content: text }));
    } catch (err) {
      console.error("Error generating response:", err);
      setMessages(prev => prev.slice(0, -1).concat({ 
        role: "assistant", 
        content: "⚠️ I encountered an error while processing your request. This might be due to high load or temporary issues. Please wait 15 seconds and try again.",
        isError: true
      }));
      setCooldownSeconds(15);
    } finally {
      setIsLoading(false);
    }
  };

  // If user is not authenticated, show login prompt
  if (!user) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-6 text-center">
        <LogIn size={48} className="text-purple-600 dark:text-purple-400 mb-4" />
        <h3 className="text-xl font-semibold mb-2 text-gray-900 dark:text-gray-100">
          Sign in to use Transcript Chat
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Chat with AI about video content and get personalized insights.
        </p>
        <button
          onClick={() => window.dispatchEvent(new CustomEvent('open-auth-modal'))}
          className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          Sign in
        </button>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col p-4">
      {error && (
        <div className="p-4 mb-4 bg-red-50 text-red-600 border rounded-lg">
          <div className="flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            {formatMessageContent(error)}
          </div>
        </div>
      )}

      {initialTranscript && (
        <div className="flex flex-col h-full">
          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">Select Profile</label>
            <select
              value={activeProfile.id}
              onChange={(e) => {
                const profile = profiles[e.target.value as keyof typeof profiles]
                if (profile) {
                  setActiveProfile(profile)
                }
              }}
              className="w-full p-2 border rounded-lg bg-white dark:bg-gray-800 dark:border-gray-700"
            >
              {Object.values(profiles).map((profile) => (
                <option key={profile.id} value={profile.id}>
                  {profile.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex-1 overflow-y-auto mb-6 space-y-6 pr-2" style={{ maxHeight: 'calc(100vh - 240px)' }}>
            {messages.map((message, index) => (
              <div key={index} className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}>
                <div 
                  className={`max-w-[80%] rounded-lg p-4 ${
                    message.role === "user" 
                      ? "bg-purple-600 text-white" 
                      : message.isError
                        ? "bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800"
                        : "bg-gray-100 dark:bg-gray-700/50"
                  }`}
                >
                  {message.isLoading ? (
                    <div className="flex items-center gap-3">
                      <div className="flex items-center space-x-1">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-100" />
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-200" />
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        Thinking... {loadingSeconds}s
                      </div>
                    </div>
                  ) : (
                    <div className="prose dark:prose-invert max-w-none space-y-4">
                      <ReactMarkdown
                        components={{
                          p: ({ children }) => {
                            const text = Array.isArray(children) 
                              ? children.map(child => typeof child === 'string' ? child : '').join('')
                              : String(children || "")
                            if (text.includes("Tweet:")) {
                              const tweets = text.split("\n").filter(line => line.trim().startsWith("Tweet:"))
                              if (tweets.length > 0) {
                                return (
                                  <div className="space-y-4">
                                    {tweets.map((tweet, idx) => {
                                      const tweetContent = tweet.replace("Tweet:", "").trim()
                                      const tweetId = `tweet-${idx}`
                                      return (
                                        <div key={tweetId} className="relative group rounded-md bg-gray-50 dark:bg-gray-900">
                                          <div className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button
                                              onClick={() => copyToClipboard(tweetContent, tweetId)}
                                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors"
                                              title="Copy tweet"
                                            >
                                              {copied[tweetId] ? (
                                                <>
                                                  <Check size={14} />
                                                  Copied!
                                                </>
                                              ) : (
                                                <>
                                                  <Copy size={14} />
                                                  Copy
                                                </>
                                              )}
                                            </button>
                                          </div>
                                          <div className="p-4 overflow-x-auto font-mono text-sm">
                                            {tweetContent}
                                          </div>
                                        </div>
                                      )
                                    })}
                                  </div>
                                )
                              }
                            }
                            return <p className="whitespace-pre-wrap leading-relaxed">{text}</p>
                          },
                          h1: ({ children }) => <h1 className="text-2xl font-bold mb-4">
                            {Array.isArray(children) 
                              ? children.map(child => typeof child === 'string' ? child : '').join('')
                              : String(children || '')}
                          </h1>,
                          h2: ({ children }) => <h2 className="text-xl font-bold mb-3">
                            {Array.isArray(children) 
                              ? children.map(child => typeof child === 'string' ? child : '').join('')
                              : String(children || '')}
                          </h2>,
                          h3: ({ children }) => <h3 className="text-lg font-bold mb-2">
                            {Array.isArray(children) 
                              ? children.map(child => typeof child === 'string' ? child : '').join('')
                              : String(children || '')}
                          </h3>,
                          ul: ({ children }) => <ul className="list-disc pl-6 space-y-2">{children}</ul>,
                          ol: ({ children }) => <ol className="list-decimal pl-6 space-y-2">{children}</ol>,
                          li: ({ children }) => {
                            const text = Array.isArray(children) 
                              ? children.map(child => typeof child === 'string' ? child : '').join('')
                              : String(children || '')
                            
                            // Check if the text contains a timestamp pattern (HH:MM:SS or MM:SS)
                            const timestampMatch = text.match(/^(?:\[)?(?:(\d{2}):)?(\d{1,2}):(\d{2})(?:\])?\s*(.*)$/);
                            if (timestampMatch) {
                              const [_, hours, minutes, seconds, content] = timestampMatch;
                              const timestamp = hours 
                                ? `${hours}:${minutes.padStart(2, '0')}:${seconds}`
                                : `${minutes}:${seconds}`;
                              
                              return (
                                <li className="mb-2 flex items-baseline">
                                  <button
                                    onClick={() => handleSeek(timestamp)}
                                    className="text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 font-medium mr-2 transition-colors whitespace-nowrap hover:underline"
                                  >
                                    {timestamp}
                                  </button>
                                  <span className="flex-1">{content}</span>
                                </li>
                              );
                            }
                            
                            return <li className="mb-2">{text}</li>;
                          },
                          blockquote: ({ children }) => (
                            <blockquote className="border-l-4 border-gray-200 dark:border-gray-700 pl-4 italic">
                              {Array.isArray(children) 
                                ? children.map(child => typeof child === 'string' ? child : '').join('')
                                : String(children || '')}
                            </blockquote>
                          ),
                          table: ({ children }) => (
                            <div className="overflow-x-auto my-4">
                              <div className="inline-block min-w-full align-middle">
                                <div className="overflow-hidden border border-gray-200 dark:border-gray-700 sm:rounded-lg">
                                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                    {children}
                                  </table>
                                </div>
                              </div>
                            </div>
                          ),
                          thead: ({ children }) => (
                            <thead className="bg-gray-50 dark:bg-gray-800">
                              {children}
                            </thead>
                          ),
                          tbody: ({ children }) => (
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-900">
                              {children}
                            </tbody>
                          ),
                          tr: ({ children }) => (
                            <tr className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                              {children}
                            </tr>
                          ),
                          th: ({ children }) => (
                            <th scope="col" className="px-4 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">
                              {Array.isArray(children) 
                                ? children.map(child => typeof child === 'string' ? child : '').join('')
                                : String(children || '')}
                            </th>
                          ),
                          td: ({ children }) => (
                            <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                              {Array.isArray(children) 
                                ? children.map(child => typeof child === 'string' ? child : '').join('')
                                : String(children || '')}
                            </td>
                          ),
                        }}
                      >
                        {formatMessageContent(message.content)}
                      </ReactMarkdown>
                    </div>
                  )}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} className="h-0" />
          </div>

          <form onSubmit={handleSubmit} className="flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={isLoading ? `Waiting for response (${loadingSeconds}s)...` : "Ask about the video..."}
              className="flex-1 p-2 border rounded-lg bg-white dark:bg-gray-800 dark:border-gray-700"
              disabled={isLoading}
            />
            <button
              type="button"
              onClick={requestTimestamps}
              disabled={isLoading || cooldownSeconds > 0}
              className="p-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center justify-center min-w-[40px]"
              title="Get video timestamps"
            >
              <Clock size={20} />
            </button>
            <button
              type="submit"
              disabled={isLoading || cooldownSeconds > 0 || !input.trim()}
              className="relative p-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 min-w-[40px]"
              title={cooldownSeconds > 0 ? `Wait ${cooldownSeconds}s` : undefined}
            >
              {cooldownSeconds > 0 ? (
                <span className="text-sm font-medium">{cooldownSeconds}</span>
              ) : (
                <Send size={20} />
              )}
              {cooldownSeconds > 0 && (
                <div 
                  className="absolute inset-0 bg-purple-700 rounded-lg opacity-20"
                  style={{
                    transform: `scaleY(${cooldownSeconds / 15})`,
                    transformOrigin: 'bottom',
                    transition: 'transform 1s linear'
                  }}
                />
              )}
            </button>
          </form>
        </div>
      )}
    </div>
  )
}
